/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React from 'react';
import Cookies from 'js-cookie';
import * as default_data from './config.js'

const use_cookies = false;

function setWithExpiry(key, value, ttl=86400000) {
  try {
    const now = new Date()

    var force_use_cookies = false;
    if(use_cookies) {
      force_use_cookies = true;
    }
    if(key === 'access_token' || key === 'saml_app_state' || key === 'saml_code_verifier' || key === 'saml_orig_url') {
      force_use_cookies = true;
    }
    const isLocal = window.location.href.indexOf('localhost') > -1;

    // `item` is an object which contains the original value
    // as well as the time when it's supposed to expire
    const item = {
      value: value,
      expiry: now.getTime() + ttl,
    }
    if(force_use_cookies) {
      var timing = new Date(new Date().getTime() + ttl);
        Cookies.set(key, JSON.stringify(item), {
        expires: timing, 
        secure: !isLocal, // Secure HTTPS
        sameSite: 'Strict' // SameSite set to "Strict"
      });    
    }
    else {
      localStorage.setItem(key, JSON.stringify(item))
    }
  }
  catch {}
}

function getWithExpiry(key) {
  try {
    var force_use_cookies = false;
    if(use_cookies) {
      force_use_cookies = true;
    }
    if(key === 'access_token' || key === 'saml_app_state' || key === 'saml_code_verifier' || key === 'saml_orig_url') {
      force_use_cookies = true;
    }

    var itemStr = null;
    if(force_use_cookies) {
      itemStr = Cookies.get(key)
    }
    else {
      itemStr = localStorage.getItem(key)
    }

    // if the item doesn't exist, return null
    if (!itemStr || itemStr == null) {
      return null
    }

    const item = JSON.parse(itemStr)
    if(item == null) {
      return null;
    }

    const now = new Date()
    // compare the expiry time of the item with the current time
    if (now.getTime() > item.expiry) {
      // If the item is expired, delete the item from storage
      // and return null
      removeCookie(key)
      return null
    }
    return item.value
  }
  catch { return null; }
}

function removeCookie(key) {
  var force_use_cookies = false
  if(use_cookies) {
    force_use_cookies = true;
  }
  if(key === 'access_token' || key === 'saml_app_state' || key === 'saml_code_verifier' || key === 'saml_orig_url') {
    force_use_cookies = true;
  }

  try {
    if(force_use_cookies) {
      Cookies.remove(key);
    }
    else {
      localStorage.removeItem(key)
    }
  }
  catch {}
}

function getUser() {    
    var user = getWithExpiry('user');
    try {
        return JSON.parse(user);
    }
    catch {
        return false;
    }
}

function updateUser(first_name, last_name, company, phone, custom_intro_prompt) {    
  var obj = getWithExpiry('user');
  try {
      var parsed_user = JSON.parse(obj);

      parsed_user.first_name = first_name;
      parsed_user.last_name = last_name;
      parsed_user.company = company;
      parsed_user.phone = phone;
      parsed_user.custom_intro_prompt = custom_intro_prompt;

      setWithExpiry('user', JSON.stringify(parsed_user))
  }
  catch {
      return false;
  }
}

function getToken() {
  try {
    var token = getWithExpiry('access_token');
    return token;
  }
  catch {
      return false;
  }
}

function isLoggedIn() {

    var user = getUser()
    if(user && user.id) {
        if(user.mfa_required && user.mfa_required == true) {
          return false;
        }
        return true;
    }
    return false;
}

function isPaid() {
  var user = getUser()
  if(user && user.id) {
      if(user.paid && user.paid == true) {
        return true;
      }
  }
  return false;
}

function isSuperAdminLoggedIn() {
  var user = getUser()
  if(user && user.id && user.type === 'superadmin') {
    if(user.cac_auth === true || user.mfa_auth === true) {
      return true;
    }
  }
  return false;
}

function isBotLoggedIn() {
  var user = getUser()
  if(user && user.id && user.type === 'bot') {
    return true;
  }
  return false;
}

function getBotSettingItem(key) {
  var user = getUser()
  if(user && user.id && user.type === 'bot') {
    if(user.bot_settings && user.bot_settings[key] !== null) {
      return user.bot_settings[key];
    }
  }
  return null;
}

function isAdminLoggedIn() {
  var user = getUser()
  if(user && user.id && user.type === 'admin') {
    if(user.cac_auth === true || user.mfa_auth === true) {
      return true;
    }
  }
  return false;
}

function loginWithToken(token) {
  
  fetch(default_data.default_user_service_url_no_cac + '/validate_token_with_full_user', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-access-tokens': `${token}`
    },        
    body: JSON.stringify({
    })
  })
    .then(response => {
      if (response.ok) {
        return response.json();
      }
      throw new Error('An error occurred.');
    })
    .then(data => {
      if(data.status != 200) {
          throw new Error('Invalid tokens');
          
      }
      else {
        setWithExpiry('access_token', token);
        setWithExpiry('loss-pass', true);
        var user = data.response;
        setWithExpiry('user', JSON.stringify(user));
        window.location = '/update?action=force_password';
      }
    })
    .catch(error => {
      return false;
    });    
}

async function createChat(chat_id, title, chat_content) {
  var token = getToken();
  if (token === false) {
    logout();
    return null;
  }

  try {
    const response = await fetch(default_data.default_user_service_url_no_cac + '/create-chat-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({session_id: chat_id, title: title, json_data: chat_content})
    });

    if (!response.ok) {
      throw new Error('An error occurred while getting data.');
    }

    const data = await response.json();
    if (data != null && data.status == 200) {
      return true;
    }
    return false;
  }
  catch {
    return false;
  }
}

async function updateChat(chat_id, title, chat_content) {
  var token = getToken();
  if (token === false) {
    logout();
    return null;
  }

  try {
    const response = await fetch(default_data.default_user_service_url_no_cac + '/update-chat-session-json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({session_id: chat_id, title: title, json_data: chat_content})
    });

    if (!response.ok) {
      throw new Error('An error occurred while getting data.');
    }

    const data = await response.json();
    if (data != null && data.status == 200) {
      return true;
    }
    return false;
  }
  catch {
    return false;
  }
}

async function deleteChat(chat_id) {
  var token = getToken();
  if (token === false) {
    logout();
    return null;
  }

  try {
    const response = await fetch(default_data.default_user_service_url_no_cac + '/delete-chat-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({session_id: chat_id})
    });

    if (!response.ok) {
      throw new Error('An error occurred while getting data.');
    }

    const data = await response.json();
    if (data != null && data.status == 200) {
      return true;
    }
    return false;
  }
  catch {
    return false;
  }
}

async function updateChatTitle(chat_id, title) {
  var token = getToken();
  if (token === false) {
    logout();
    return null;
  }

  try {
    const response = await fetch(default_data.default_user_service_url_no_cac + '/update-chat-session-title', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({session_id: chat_id, new_title: title})
    });

    if (!response.ok) {
      throw new Error('An error occurred while getting data.');
    }

    const data = await response.json();
    if (data != null && data.status == 200) {
      return true;
    }
    return false;
  }
  catch {
    return false;
  }
}

async function getChats() {
  var token = getToken();
  if (token === false) {
    logout();
    return null;
  }

  try {
    const response = await fetch(default_data.default_user_service_url_no_cac + '/get-chats', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-tokens': `${token}`
      },
      body: JSON.stringify({})
    });

    if (!response.ok) {
      throw new Error('An error occurred while getting data.');
    }

    const data = await response.json();
    if (data != null && data.status == 200) {
      var res = data.response;
      if (res != null) {
        try {
          var chats = JSON.parse(res);
          return chats;
        }
        catch {
          return null;
        }
      } 
    }
    return null;
  }
  catch {
    return null;
  }
}

function pushChats(chats, buttons) {

  var token = getToken()
  if(token === false) {
    return;
  }
  try {
    fetch(default_data.default_user_service_url_no_cac + '/update-chats', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-tokens': `${token}`
        },   
        body: JSON.stringify({
          chats: {chats: chats, buttons: buttons}
        })
      })
        .then(response => {
          if (response.ok) {
            return response.json();
          }
          throw new Error('An error occurred while sending chats.');
        })
        .then(data => {
          if(data.status != 200) {
          }
        })
        .catch(error => {

        });
  }
  catch {}
}

function logout() {

    var token = getToken()
    removeCookie('access_token');
    removeCookie('loss-pass')
    removeCookie('user');
    if(token === false) {
      window.location = '/login';
      return;
    }
    fetch(default_data.default_user_service_url_no_cac + '/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-tokens': `${token}`
        },        
        body: JSON.stringify({
        })
      })
        .then(response => {
          if (response.ok) {
            return response.json();
          }
          throw new Error('An error occurred while logging out.');
        })
        .then(data => {
          if(data.status != 200) {
              console.log(data.response);
          }
          throw new Error('No problem');
        })
        .catch(error => {
          window.location = '/login';
        });
}

function getName() {

    if(!isLoggedIn()) {
        return ""
    }
    return(getUser().first_name)
}

export { isLoggedIn, isAdminLoggedIn, isBotLoggedIn, getBotSettingItem, isSuperAdminLoggedIn, getName, logout, getToken, getWithExpiry, setWithExpiry, pushChats, getUser, updateUser, loginWithToken, isPaid, getChats, updateChatTitle, deleteChat, createChat, updateChat}